import type { IImage, ISelection, IGallery, IndexedImage } from 'old-store/types/gallery';
import { getSortedAndfilteredImgs } from 'helpers/sorting';

export const filterImagesByGalleries = (
  galleries: IGallery[],
  images: IImage[]
): {
  [key: string]: IndexedImage[];
} => {
  const checkIfGifFile = (img: IImage) => img.extension.toLowerCase() === 'gif';
  const checkCompressionStatus = (img: IImage) =>
    checkIfGifFile(img) || (!checkIfGifFile(img) && img.compressionStatus === 'done');
  const checkIfWidthAndHeightExists = (img: IImage) => Boolean(img?.width) && Boolean(img?.height);

  const sortedImagesByGalleries = galleries?.reduce((acc, gallery) => {
    const checkIfSameGallery = (img: IImage) => img.gallery_id === gallery._id;

    const imgFilter = (img: IImage) =>
      checkIfSameGallery(img) && checkCompressionStatus(img) && checkIfWidthAndHeightExists(img);
    const sorting = { sortBy: gallery.sortBy, sortOrder: gallery.sortOrder };

    const sortedImgs = getSortedAndfilteredImgs(images, imgFilter, sorting);
    const sortedTitleImgs = sortedImgs.map((img, idx) => ({
      ...img,
      galleryTitle: gallery.title,
      id: idx,
      index: idx,
      src: img.url,
      selectionId: '',
      imageId: `${gallery._id}_${idx}`
    }));

    // @ts-ignore
    acc[gallery._id] = sortedTitleImgs;

    return acc;
  }, {});

  return sortedImagesByGalleries;
};

export const filterEmptyShareGalleries = <T extends { _id: string }>(
  galleries: T[],
  filteredImagesMap: { [key: string]: unknown[] },
  isShareSite: boolean | null
): T[] => {
  if (!isShareSite) return galleries;

  // Images may still be loading or compressing; filtering on an empty map would
  // blank the page. Wait until at least one image is present before hiding galleries.
  const hasAnyImages = Object.values(filteredImagesMap).some((imgs) => imgs.length > 0);
  if (!hasAnyImages) return galleries;

  return galleries.filter((gallery) => (filteredImagesMap[gallery._id]?.length || 0) > 0);
};

export const filterImagesBySelections = (
  images: IImage[],
  all: ISelection[]
): {
  [key: string]: IImage[];
} => {
  const favoritesMap = all?.reduce((acc, item) => {
    const favoritesSet = new Set(item.favorites.map(({ _image }) => _image));

    // SBK-8154: keep the incoming gallery order (images arrive sorted per gallery
    // by filterImagesByGalleries) instead of the order the customer liked them in
    // @ts-ignore
    const mappedImages = images
      .filter((img) => favoritesSet.has(img._id))
      .map((image, idx) => ({
        ...image,
        selectionId: `${item._id}_${idx}`,
        selectionIndex: idx,
        src: image.url
      }));

    acc[item._id] = mappedImages;

    return acc;
  }, {});

  return favoritesMap;
};
